import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Home, CircleDot, Trophy, History, BarChart3, Settings, LogOut } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import mtlogo from '@/assets/images/mt-logo.png'
import { supabase } from '@/lib/supabase';
import { getFirstName } from '@/lib/utils';

export default function LayoutV1({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user, signOut } = useAuth();
  const [competitions, setCompetitions] = useState<any[]>([]);
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [showProgress, setShowProgress] = useState(false);

  useEffect(() => {
    const fetchCompetitions = async () => {
      const { data } = await supabase
        .from('competitions')
        .select('id,name,is_active')
        .order('created_at', { ascending: true });

      const list = data || [];
      setCompetitions(list);
      const active = list.find((comp) => comp.is_active) || list[0];
      if (active) {
        setSelectedCompetition(active.id);
      }
    };

    fetchCompetitions();
  }, []);

  useEffect(() => {
    setShowProgress(true);
    const timeout = window.setTimeout(() => setShowProgress(false), 700);
    return () => window.clearTimeout(timeout);
  }, [location]);

  const handleCompetitionChange = async (competitionId: string) => {
    setSelectedCompetition(competitionId);

    const { error: activateError } = await supabase
      .from('competitions')
      .update({ is_active: true })
      .eq('id', competitionId);

    if (activateError) {
      return;
    }

    await supabase
      .from('competitions')
      .update({ is_active: false })
      .neq('id', competitionId);

    window.dispatchEvent(new Event('competition-changed'));
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-sm border-b border-slate-800 sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            {/* Logo & Title */}
            <Link href="/">
              <a className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80 transition-opacity">
                <img src={mtlogo} alt="MatchTips" className="w-24 sm:w-32 object-contain" />
              </a>
            </Link>

            {/* Competition Selector - Hidden on small mobile */}
            <div className="hidden sm:block flex-1 max-w-50 md:max-w-60">
              <Select value={selectedCompetition} onValueChange={handleCompetitionChange}>
                <SelectTrigger className="w-full bg-slate-800 border-slate-700 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {competitions.map(comp => (
                    <SelectItem key={comp.id} value={comp.id}>
                      {comp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* User Info & Logout */}
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-slate-400 hidden md:inline">
                {getFirstName(user?.name, user?.email, 'User')}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => signOut()}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="container mx-auto px-2 sm:px-4 py-0">
          <div className="flex gap-0.5 sm:gap-1 justify-around sm:justify-start overflow-x-auto">
            <NavLink href="/dashboard" icon={Home} label="Home" active={location === '/dashboard'} />
            <NavLink href="/active" icon={CircleDot} label="Round" active={location === '/active'} />
            <NavLink href="/leaderboard" icon={Trophy} label="Leaderboard" active={location === '/leaderboard'} />
            <NavLink href="/rounds" icon={History} label="History" active={location === '/rounds'} />
            <NavLink href="/stats" icon={BarChart3} label="Stats" active={location === '/stats'} />
            
            {user?.isAdmin && (
              <NavLink href="/admin" icon={Settings} label="Admin" active={location === '/admin'} />
            )}
          </div>
        </nav>
      </header>

      {showProgress && (
        <div className="h-0.5 w-full bg-gradient-to-r from-blue-500 via-blue-300 to-blue-500 animate-pulse" />
      )}

      {/* Main Content */}
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        {children}
      </main>
    </div>
  );
}

function NavLink({ href, icon: Icon, label, active }: { href: string; icon: any; label: string; active: boolean }) {
  return (
    <Link href={href}>
      <a
        className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 md:px-4 py-2 sm:py-3 rounded-t-lg transition-colors whitespace-nowrap shrink-0 ${
          active
            ? 'bg-slate-800 text-white border-b-2 border-blue-500'
            : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
        }`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="text-[10px] sm:text-sm font-medium">{label}</span>
      </a>
    </Link>
  );
}
